import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Alert,
  Button,
  Link,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Loader from "../components/common/custom-component/Loader";
import { fetchUserManualsDataThunk } from "../store/thunks/mdsThunks";

export default function UserManualsPage() {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [manualTree, setManualTree] = useState([]);
  const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(true);
  const [error, setError] = useState(null);
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    let promiseRef = null;

    const fetchData = async () => {
      setError(null);
      setIsLoadingBackdrop(true);

      promiseRef = dispatch(fetchUserManualsDataThunk());

      const result = await promiseRef;

      if (fetchUserManualsDataThunk.fulfilled.match(result)) {
        const parents = Array.isArray(result?.payload?.data) ? result?.payload?.data : [];

        setManualTree(parents);

        setIsLoadingBackdrop(false);
      } else if (fetchUserManualsDataThunk.rejected.match(result)) {
        if (result?.payload !== "cancelled") {
          setError(result?.payload || "An error occurred while loading data");
        }
        setIsLoadingBackdrop(false);
      }
    };

    fetchData();

    return () => {
      if (promiseRef) {
        console.log("🛑 Component unmounting, aborting request");
        promiseRef.abort();
      }
    };
  }, [dispatch, retryTrigger]);

  const handleRetry = () => setRetryTrigger((p) => p + 1);

  const Parents = useMemo(() => {
    return (manualTree || []).map((parent) => {
      const children = Array.isArray(parent?.children) ? parent.children : [];
      return { ...parent, children };
    });
  }, [manualTree]);

  if (error) {
    return (
      <Box
        sx={{
          p: 4,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ width: "100%", maxWidth: 650 }}>
          {error}
        </Alert>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (isLoadingBackdrop) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Loader variant="circular" message="Loading your data..." size="small" />
      </Box>
    );
  }

  if (!Parents.length) {
    return (
      <Box sx={{ p: 4, mx: "auto" }}>
        <Typography variant="h3" gutterBottom color="primary">
          {t("User Manual")}
        </Typography>

        <Alert severity="info" sx={{ mt: 2 }}>
          No manuals found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, mx: "auto" }}>
      <Typography variant="h3" gutterBottom color="primary">
        {t("User Manual")}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={4}>
        {Parents.map((parent) => (
          <Grid item xs={12} md={6} key={parent.id}>
            {/* Parent title*/}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
            >
              {parent?.title}
            </Typography>

            {/* Children links */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(parent?.children || []).map((child) => (
                <Box
                  key={child.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 0.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <DownloadOutlinedIcon color="primary" />

                  <Link
                    href={child?.to || "#"}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ fontSize: 18, fontWeight: 500 }}
                    onClick={(e) => {
                      if (!child?.to) e.preventDefault();
                    }}
                  >
                    {child?.title || "Download"}
                  </Link>
                </Box>
              ))}

              {/* If parent has no children*/}
              {(!parent?.children || parent.children.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No files available.
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
